import {assert} from 'Asserts';
import {AxisOrientation, XAxisOrientation} from 'ui-library/charting/internals/Axis';
import {Margin, VisxScaleType} from 'ui-library/charting/internals/VisxUtilityTypes';

import getTickLabelWidths from './getTickLabelWidths';
import {VisxAxisConfig} from './getVisxAxisConfig';
import {
  AXIS_LABEL_TO_TICK_LABEL,
  DEFAULT_X_AXIS_MARGIN_WITHOUT_LABEL,
  DEFAULT_X_AXIS_MARGIN_WITH_LABEL,
  LABEL_FONT_SIZE,
  ROTATED_TICK_LABEL_TO_AXIS,
  TICK_LABEL_TO_CHART_EDGE,
} from './Theme';

export interface GetXTickLabelRotationAndMarginsInput {
  currentMargins: Margin;
  totalWidth: number;
  xAxisConfig: VisxAxisConfig;
  xAxisOrientation: XAxisOrientation;
}

export interface GetXTickLabelRotationAndMarginsOutput {
  margins: Margin;
  rotation: number;
}

/**
 * Compute x-axis tick label rotation, and the chart margins from those rotations.
 *
 * These two concepts are grouped together because margins depend on the rotation: as labels rotate, the
 * margins need to be adjusted to fit them.
 *
 * Assumes tick labels are configured to span the domain, so the first and last ticks are on the leftmost and
 * rightmost edges of the chart.
 *
 * Returns the rotation in degrees, and the margins the chart should use.
 *
 * The rotation is calculated as follows:
 *   1. Calculate the max amount of space each label can take, assuming space is spread evenly
 *   2. If the max width is smaller than the result of 2, no rotation is needed
 *   3. Otherwise, return the angle needed to rotate the largest label to fit into 2
 *
 * The margins are calculated as follows:
 *   1. If there is no rotation:
 *     a. Labels will be anchored at their center, so the left margin needs to fit the first label, and the
 *        right margin needs to fit the last label
 *   2. If there is rotation:
 *     a. When the x-axis is at the bottom of the chart:
 *       i. Labels will be anchored at their end, so the left margin needs to fit the first label
 *       ii. The bottom margin needs to fit the angled labels, plus the axis label when present
 *     b. When the x-axis is at the top of the chart:
 *       i. Labels will be anchored at their start, so the right margin needs to fit the last label
 *       ii. The top margin needs to fit the angled labels, plus the axis label when present
 */
export default function getXTickLabelRotationAndMargins({
  currentMargins,
  totalWidth: tentativeTotalWidth,
  xAxisConfig,
  xAxisOrientation,
}: GetXTickLabelRotationAndMarginsInput): GetXTickLabelRotationAndMarginsOutput {
  const tickLabelWidths = getTickLabelWidths(xAxisConfig);

  const widthToFit = Math.max(...tickLabelWidths);

  // Band axes render the tick in the center of the "band", which means the first tick isn't on the
  // on left most part of the x-axis and the last tick isn't on the right most part of the x-axis. Instead,
  // they're availableWidthPerTickLabel/2 away from both points. Therefore, we need to use a totalWidth that
  // is a bit smaller than the axis's width; otherwise, we'll start rotating ticks too late and end up with
  // some ticks that overlap each other on certain chart widths.
  //
  // To calculate this more accurate totalWidth for band axes, we need to use a totalWidth that is padded by
  // availableWidthPerTickLabel/2 on each side, or in other words, totalWidth - availableWidthPerTickLabel.
  // However, since availableWidthPerTickLabel is calculated based on the totalWidth, we need to calculate
  // an estimate availableWidthPerTickLabel to use to calculate the totalWidth. One easy way to do that is to
  // simply use the totalWidth before we pad it. It does cause the ticks to start rotating a bit too early
  // in some cases because it's an overestimate, which wastes space, but it guarantees that we don't have
  // overlapping ticks in all the cases I tested.
  const estimateAvailableWidthPerTickLabel = tentativeTotalWidth / tickLabelWidths.length;
  const totalWidth =
    xAxisConfig.type === VisxScaleType.BAND && tickLabelWidths.length > 1
      ? tentativeTotalWidth - estimateAvailableWidthPerTickLabel
      : tentativeTotalWidth;
  const availableWidthPerTickLabel = totalWidth / tickLabelWidths.length;

  const [firstTickLabelWidth, lastTickLabelWidth] = [
    tickLabelWidths[0],
    tickLabelWidths[tickLabelWidths.length - 1],
  ];

  if (widthToFit <= availableWidthPerTickLabel) {
    return getOutputWithoutRotation(currentMargins, xAxisConfig, xAxisOrientation, [
      firstTickLabelWidth,
      lastTickLabelWidth,
    ]);
  }

  return getOutputWithRotation(
    currentMargins,
    xAxisConfig,
    xAxisOrientation,
    [firstTickLabelWidth, lastTickLabelWidth],
    widthToFit,
    availableWidthPerTickLabel
  );
}

export function getOutputWithoutRotation(
  currentMargins: Margin,
  {label}: VisxAxisConfig,
  xAxisOrientation: XAxisOrientation,
  [firstTickLabelWidth, lastTickLabelWidth]: [number, number]
): GetXTickLabelRotationAndMarginsOutput {
  // Labels will be anchored at their center, so half of their width will go beyond the chart boundaries
  const leftMargin = Math.max(firstTickLabelWidth / 2 + TICK_LABEL_TO_CHART_EDGE, currentMargins.left);
  const rightMargin = Math.max(lastTickLabelWidth / 2 + TICK_LABEL_TO_CHART_EDGE, currentMargins.right);

  const xAxisMargin =
    label === undefined ? DEFAULT_X_AXIS_MARGIN_WITHOUT_LABEL : DEFAULT_X_AXIS_MARGIN_WITH_LABEL;

  return {
    rotation: 0,
    margins: {
      ...currentMargins,
      left: leftMargin,
      right: rightMargin,
      ...(xAxisOrientation === AxisOrientation.BOTTOM
        ? {
            bottom: xAxisMargin,
          }
        : {top: xAxisMargin}),
    },
  };
}

/**
 * Return output for rotated labels.
 *
 * See __docs__/RotatedXTickLabelDiagram.svg for a visual reference on how the variables here are computed.
 *
 * Expects widthToFit to be greater than availableWidthPerTickLabel.
 */
export function getOutputWithRotation(
  currentMargins: Margin,
  {label, type: scaleType}: VisxAxisConfig,
  xAxisOrientation: XAxisOrientation,
  [firstTickLabelWidth, lastTickLabelWidth]: [number, number],
  widthToFit: number,
  availableWidthPerTickLabel: number
): GetXTickLabelRotationAndMarginsOutput {
  assert(
    widthToFit > availableWidthPerTickLabel,
    'Tried to rotate tick labels when they should not be rotated'
  );

  const tickLabelRotation = Math.acos(availableWidthPerTickLabel / widthToFit);
  const cos = Math.abs(Math.cos(tickLabelRotation));
  const sin = Math.abs(Math.sin(tickLabelRotation));

  const rotatedFontExtraHeight = cos * LABEL_FONT_SIZE;
  const rotatedTickLabelHeight = sin * widthToFit;
  const totalTickLabelHeight = rotatedFontExtraHeight + rotatedTickLabelHeight;

  const rotatedFontExtraWidth = sin * LABEL_FONT_SIZE;

  const xAxisMargin =
    ROTATED_TICK_LABEL_TO_AXIS +
    totalTickLabelHeight +
    (label === undefined ? 0 : AXIS_LABEL_TO_TICK_LABEL + LABEL_FONT_SIZE);

  // When the first/last (depending on axis being on bottom/top) tick label is long enough, it will render outside
  // the charting area, so we will need to adjust the left/right margins to fit it.
  //
  // This is calculated like so:
  //    (left/right)Margin =
  //        TICK_LABEL_TO_CHART_EDGE
  //        + rotated(First/Last)TickLabelWidth
  //        + rotatedFontExtraWidth
  //        - (adjustmentForBandScale**)
  //
  // We will choose the max of (left/right)Margin and currentMargins.(left/right) as the output left/right margin.
  //
  // **Since band scales usually render the tick label in the center of the band. The first/last tick labels will
  // not be anchored directly at the start/end of the axis and instead will be anchored in the center of the
  // band, so we additionally need to remove availableWidthPerTickLabel / 2 from the (left/right)Margin for band
  // scale axes.
  const baseExtraLeftRightMargin =
    TICK_LABEL_TO_CHART_EDGE - (scaleType === VisxScaleType.BAND ? availableWidthPerTickLabel / 2 : 0);

  if (xAxisOrientation === AxisOrientation.TOP) {
    const rotatedLastTickLabelWidth = cos * lastTickLabelWidth;
    const totalLastTickLabelWidth = rotatedFontExtraWidth + rotatedLastTickLabelWidth;
    const rightMargin = Math.max(totalLastTickLabelWidth + baseExtraLeftRightMargin, currentMargins.right);

    return {
      rotation: (-1 * (tickLabelRotation * 180)) / Math.PI,
      margins: {
        ...currentMargins,
        top: xAxisMargin,
        right: rightMargin,
      },
    };
  } else {
    const rotatedFirstTickLabelWidth = cos * firstTickLabelWidth;
    const totalFirstTickLabelWidth = rotatedFontExtraWidth + rotatedFirstTickLabelWidth;
    const leftMargin = Math.max(totalFirstTickLabelWidth + baseExtraLeftRightMargin, currentMargins.left);

    return {
      rotation: (tickLabelRotation * 180) / Math.PI,
      margins: {
        ...currentMargins,
        bottom: xAxisMargin,
        left: leftMargin,
      },
    };
  }
}
